import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import GuideSearch from "./GuideSearch";

const REGIONS_DATA = [
  {
    id: "tashkent",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Amir-Timur-Museum%20Taschkent%2001.jpg?width=1200",
  },
  {
    id: "samarkand",
    image:
      "https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?auto=format&fit=crop&w=1200&q=80",
  },
  {
    id: "bukhara",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Po-i-kalyan-complex.jpg?width=1200",
  },
  {
    id: "khorezm",
    image:
      "https://images.unsplash.com/photo-1605649487212-47bdab064df7?auto=format&fit=crop&w=1200&q=80",
  },
  {
    id: "andijan",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Andijon%20jome.jpg?width=1200",
  },
  {
    id: "namangan",
    image:
      "https://storage.kun.uz/source/6/dvKfHZe_azrk1_3z1mtc0JUQMwuKg2oM.jpg",
  },
  {
    id: "fergana",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Khudayar%20Khan%20Palace%2C%20Kokand%2001.JPG?width=1200",
  },
  {
    id: "jizzakh",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Zaamin%20National%20Park%2C%20Uzbekistan.jpg?width=1200",
  },
  {
    id: "kashkadarya",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Shakhrisabz%20Aksarai.jpg?width=1200",
  },
  {
    id: "surkhandarya",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Fayoztepa%20gumbaz.jpg?width=1200",
  },
  {
    id: "navoiy",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Rabat-i%20Malik%20caravanserai%201.JPG?width=1200",
  },
  {
    id: "syrdarya",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Syr%20Darya%20river.jpg?width=1200",
  },
  {
    id: "karakalpakstan",
    image:
      "https://commons.wikimedia.org/wiki/Special:FilePath/Chilpiq.JPG?width=1200",
  },
];

function RegionCard({ id, image }) {
  const { t } = useTranslation();

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col hover:shadow-md transition-all">
      <Link
        to={`/regions/${id}`}
        className="group block h-28 sm:h-36 md:h-44 overflow-hidden bg-slate-100 dark:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-400"
        aria-label={t(`regions.${id}.name`)}
      >
        <img
          src={image}
          alt={t(`regions.${id}.name`)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </Link>

      <div className="p-3 sm:p-4 md:p-5 flex flex-col flex-1">
        <h3 className="font-serif text-sm sm:text-base md:text-lg font-bold text-slate-900 dark:text-white mb-1.5 sm:mb-2 leading-snug">
          {t(`regions.${id}.name`)}
        </h3>
        <p className="hidden sm:block text-slate-500 dark:text-slate-400 text-xs md:text-sm leading-relaxed mb-3 md:mb-4 line-clamp-2">
          {t(`regions.${id}.description`)}
        </p>


        <div className="mt-auto pt-2 sm:pt-3 md:pt-4 border-t border-slate-100 dark:border-slate-800">
          <Link
            to={`/regions/${id}`}
            className="inline-flex items-center gap-1 sm:gap-2 bg-slate-900 dark:bg-amber-500 text-white dark:text-slate-950 text-xs md:text-sm font-semibold rounded-full pl-3 pr-1 sm:pl-5 sm:pr-1.5 py-1 sm:py-1.5 hover:bg-slate-800 dark:hover:bg-amber-400 transition-colors"
          >
            <span className="truncate">{t("regions_page.explore", "Исследовать")}</span>
            <span className="w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-amber-400 dark:bg-slate-950 flex items-center justify-center text-slate-900 dark:text-amber-400 shrink-0 text-xs sm:text-base">
              →
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}

const CitiesGrid = () => {
  const { t } = useTranslation();

  return (
    <section className="bg-[#F7F6F3] dark:bg-slate-950 px-4 sm:px-6 md:px-8 py-12 sm:py-16 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-8 sm:mb-10 md:mb-12">
          <div>
            <span className="block text-xs font-bold tracking-[0.2em] sm:tracking-[0.25em] text-amber-500 mb-2 sm:mb-3">
              {t("regions_page.badge")}
            </span>
            <div className="w-10 h-0.5 bg-amber-400 mb-2 sm:mb-3 order-first md:order-none" />
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              {t("regions_page.title")}
            </h2>
          </div>
          <p className="text-slate-500 dark:text-slate-400 max-w-sm text-sm md:text-[15px] leading-relaxed">
            {t("regions_page.subtitle")}
          </p>
        </div>

        <GuideSearch />

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {REGIONS_DATA.map((region) => (
            <RegionCard key={region.id} id={region.id} image={region.image} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CitiesGrid;
